import { createContext, useContext, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlarmClock,
  ArrowDown,
  ArrowUp,
  Ban,
  BarChart3,
  Bot,
  Brain,
  Check,
  Crosshair,
  FilterX,
  Gauge,
  HelpCircle,
  History,
  ListFilter,
  Lock,
  Navigation,
  PanelRight,
  PauseCircle,
  Play,
  RotateCcw,
  SearchX,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  TrendingDown,
  TrendingUp,
  Wallet,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  getAgentDemo,
  parseAgentCountry,
  SEED_TURNS,
  type AgentCountry,
  type AgentDemo,
  type AgentOpportunity,
  type AgentRule,
  type AgentTurn,
} from '@/components/agent/agentDemo';

const AgentDemoContext = createContext<AgentDemo | null>(null);

function useAgentDemo(): AgentDemo {
  const demo = useContext(AgentDemoContext);
  if (!demo) throw new Error('Agent demo is missing');
  return demo;
}

const CHIP_ICONS: Record<string, LucideIcon> = {
  'Show what changed today': History,
  'Pause brand campaign changes': PauseCircle,
  'Why did ACOS increase yesterday?': BarChart3,
  'Set max daily budget increase to $50': SlidersHorizontal,
  'Set max daily budget increase to ₹4,000': SlidersHorizontal,
};

const RULE_ICONS: Record<string, LucideIcon> = {
  acos: Crosshair,
  scale: TrendingUp,
  waste: ListFilter,
  risk: ShieldCheck,
};

const OPPORTUNITY_ICONS: Record<string, LucideIcon> = {
  'bid-1': TrendingDown,
  'budget-1': Wallet,
  'neg-1': Ban,
};

const REASON_ICONS: Record<string, LucideIcon> = {
  'bid-1': Brain,
  'budget-1': AlarmClock,
  'neg-1': SearchX,
};

function formatClock(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function AgentPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const country = parseAgentCountry(searchParams.get('country'));
  const demo = getAgentDemo(country);
  const [turns, setTurns] = useState<AgentTurn[]>(SEED_TURNS);
  const [draft, setDraft] = useState('');
  const [approved, setApproved] = useState<Record<string, boolean>>({});
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});
  const [explained, setExplained] = useState<Record<string, boolean>>({});
  const [agentActive, setAgentActive] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hourlySync, setHourlySync] = useState(true);
  const [focusKey, setFocusKey] = useState(0);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const previousCountry = useRef(country);

  useEffect(() => {
    if (previousCountry.current === country) return;
    previousCountry.current = country;
    setTurns(SEED_TURNS);
    setDraft('');
    setApproved({});
    setDismissed({});
    setExplained({});
    setAgentActive(false);
    setHourlySync(true);
    setDrawerOpen(false);
  }, [country]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const atSeed = turns.length === SEED_TURNS.length && turns[0]?.id === 'greeting';
    scroller.scrollTop = atSeed ? 0 : scroller.scrollHeight;
  }, [turns]);

  const insertPrompt = (text: string) => {
    setDraft(text);
    setFocusKey((key) => key + 1);
  };

  const send = (text?: string) => {
    const content = (text ?? draft).trim();
    if (!content) return;
    const time = formatClock(new Date());
    const id = String(Date.now());
    setTurns((prev) => [
      ...prev,
      { id: `${id}-user`, role: 'user', text: content, time },
      { id: `${id}-agent`, role: 'agent', text: demo.agentReply, time },
    ]);
    setDraft('');
  };

  const resetDemo = () => {
    setTurns(SEED_TURNS);
    setDraft('');
    setApproved({});
    setDismissed({});
    setExplained({});
    setAgentActive(false);
    setHourlySync(true);
    setDrawerOpen(false);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    send();
  };

  const selectCountry = (next: AgentCountry) => {
    const params = new URLSearchParams(searchParams);
    params.set('country', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <AgentDemoContext.Provider value={demo}>
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-page text-foreground">
      <StatusRibbon
        agentActive={agentActive}
        onCountryChange={selectCountry}
        onOpenGuardrails={() => setDrawerOpen(true)}
        onReset={resetDemo}
      />

      <div ref={scrollerRef} className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-6 pb-8">
          <SessionDivider />
          {turns.map((turn) => (
            <Turn
              key={turn.id}
              turn={turn}
              agentActive={agentActive}
              approved={approved}
              dismissed={dismissed}
              explained={explained}
              onInsertPrompt={insertPrompt}
              onActivate={() => setAgentActive(true)}
              onOpenGuardrails={() => setDrawerOpen(true)}
              onApprove={(id) => setApproved((prev) => ({ ...prev, [id]: true }))}
              onDismiss={(id) => setDismissed((prev) => ({ ...prev, [id]: true }))}
              onToggleExplain={(id) =>
                setExplained((prev) => ({ ...prev, [id]: !prev[id] }))
              }
            />
          ))}
        </div>
      </div>

      <Composer
        draft={draft}
        focusKey={focusKey}
        onChange={setDraft}
        onInsertPrompt={insertPrompt}
        onSubmit={onSubmit}
      />

      <GuardrailsDrawer
        open={drawerOpen}
        agentActive={agentActive}
        hourlySync={hourlySync}
        onHourlySyncChange={setHourlySync}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
    </AgentDemoContext.Provider>
  );
}

function StatusRibbon({
  agentActive,
  onCountryChange,
  onOpenGuardrails,
  onReset,
}: {
  agentActive: boolean;
  onCountryChange: (country: AgentCountry) => void;
  onOpenGuardrails: () => void;
  onReset: () => void;
}) {
  const { account, country } = useAgentDemo();
  return (
    <div className="shrink-0 border-b border-border bg-surface px-6 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-ink text-chrome-text">
              <Bot className="h-[18px] w-[18px]" aria-hidden />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-[16px] font-semibold leading-6">AdPilot AI</span>
                <span className="text-body-sm text-foreground-subtle">Amazon Ads Agent</span>
              </div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                </span>
                <span className="font-mono text-[11px] text-foreground-muted">
                  {agentActive ? 'Enforcing' : 'Monitoring active'} · Stream connected (
                  {account.streamAgo})
                </span>
              </div>
            </div>
          </div>
          <div className="hidden items-center gap-2 lg:flex">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-subtle px-2.5 py-1 text-body-sm font-medium text-brand-ink">
              <Target className="h-3.5 w-3.5 text-brand" aria-hidden />
              Target ACOS:{' '}
              <strong className="font-mono font-semibold">{account.targetAcos}</strong>
              <span className="font-normal text-foreground-muted">(Drafting rules)</span>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-muted px-2.5 py-1 text-body-sm font-medium text-foreground-muted">
              <Gauge className="h-3.5 w-3.5 text-success" aria-hidden />
              Daily pace:{' '}
              <strong className="font-mono font-semibold text-foreground">
                {account.dailyPace}
              </strong>{' '}
              on track
            </span>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div
            role="group"
            aria-label="Demo marketplace"
            className="inline-flex rounded-lg border border-border bg-surface-muted p-0.5"
          >
            {(['US', 'IN'] as const).map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed={country === code}
                onClick={() => onCountryChange(code)}
                className={cn(
                  'rounded-md px-2.5 py-1 font-mono text-[12px] font-semibold',
                  country === code
                    ? 'bg-brand-ink text-white'
                    : 'text-foreground-muted hover:bg-surface hover:text-foreground'
                )}
              >
                {code}
              </button>
            ))}
          </div>
          <div className="hidden text-right sm:block">
            <p className="text-body-sm font-semibold">{account.name}</p>
            <p className="font-mono text-[11px] text-foreground-subtle">
              {account.marketplace} · {account.activeCampaigns} active campaigns
            </p>
          </div>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-body-sm font-medium text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            Reset demo
          </button>
          <button
            type="button"
            onClick={onOpenGuardrails}
            className="inline-flex items-center gap-1.5 rounded-lg bg-surface-muted px-3 py-1.5 text-body-sm font-medium shadow-sm hover:bg-border"
          >
            <Shield className="h-4 w-4 text-brand" aria-hidden />
            Guardrails
            <PanelRight className="h-4 w-4 text-foreground-subtle" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}

function SessionDivider() {
  return (
    <div className="flex items-center gap-3">
      <span className="h-px flex-1 bg-border" />
      <span className="rounded-full bg-surface-muted px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-subtle">
        Today · Live session
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

function Turn({
  turn,
  agentActive,
  approved,
  dismissed,
  explained,
  onInsertPrompt,
  onActivate,
  onOpenGuardrails,
  onApprove,
  onDismiss,
  onToggleExplain,
}: {
  turn: AgentTurn;
  agentActive: boolean;
  approved: Record<string, boolean>;
  dismissed: Record<string, boolean>;
  explained: Record<string, boolean>;
  onInsertPrompt: (text: string) => void;
  onActivate: () => void;
  onOpenGuardrails: () => void;
  onApprove: (id: string) => void;
  onDismiss: (id: string) => void;
  onToggleExplain: (id: string) => void;
}) {
  const { account, opportunities, quickReplies, user, userGoal } = useAgentDemo();
  if (turn.role === 'user' || (turn.role === 'seed' && turn.kind === 'user-goal')) {
    const text = turn.role === 'user' ? turn.text : userGoal;
    const time = turn.role === 'user' ? turn.time : '09:42 AM';
    return <UserBubble name={user.name} initials={user.initials} time={time} text={text} />;
  }

  if (turn.role === 'agent') {
    return <AgentText time={turn.time} text={turn.text} />;
  }

  if (turn.kind === 'greeting') {
    return (
      <AgentBlock time="09:41 AM">
        <div className="space-y-2 rounded-2xl rounded-tl-sm border border-border bg-surface p-5 text-body-lg shadow-sm">
          <p>
            Hi <strong>{user.name.split(' ')[0]}</strong>. I continuously analyze Sponsored
            Products, Brands, and Display campaigns, search term reports, and hourly Amazon Marketing
            Stream events.
          </p>
          <p className="text-body-md text-foreground-muted">
            What strategic direction should I prioritize for the {account.name} catalog?
          </p>
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          {quickReplies.map((reply) => (
            <button
              key={reply.title}
              type="button"
              onClick={() => onInsertPrompt(reply.prompt)}
              className="rounded-xl border border-border bg-surface p-4 text-left shadow-sm transition-colors hover:bg-brand-subtle"
            >
              <span className="mb-1 flex items-center justify-between gap-2">
                <span className="font-display text-[13px] font-semibold">{reply.title}</span>
                <QuickReplyIcon title={reply.title} />
              </span>
              <span className="block text-body-sm text-foreground-muted">{reply.body}</span>
            </button>
          ))}
        </div>
      </AgentBlock>
    );
  }

  if (turn.kind === 'rules') {
    return (
      <AgentBlock time="09:42 AM" badge="Synthesized policy">
        <div className="rounded-2xl rounded-tl-sm border border-border bg-surface p-4 text-body-lg shadow-sm">
          I translated that into a draft set of rules for this catalog. Confirm the parameters
          before any autonomous execution.
        </div>
        <RulesCard
          agentActive={agentActive}
          onActivate={onActivate}
          onOpenGuardrails={onOpenGuardrails}
        />
      </AgentBlock>
    );
  }

  const visible = opportunities.filter((item) => !dismissed[item.id]);
  return (
    <AgentBlock time="09:43 AM" badge="Live discovery">
      <div className="rounded-2xl rounded-tl-sm border border-border bg-surface p-4 text-body-lg shadow-sm">
        Here is the current picture across{' '}
        <strong>{account.activeCampaigns} active campaigns</strong>.{' '}
        {visible.length === opportunities.length
          ? `I found ${opportunities.length} immediate opportunities that match this strategy.`
          : `${visible.length} of those opportunities are still open.`}
      </div>
      <PulseCard />
      {visible.map((item) => (
        <OpportunityCard
          key={item.id}
          item={item}
          approved={Boolean(approved[item.id])}
          explained={Boolean(explained[item.id])}
          onApprove={() => onApprove(item.id)}
          onDismiss={item.dismissLabel ? () => onDismiss(item.id) : undefined}
          onToggleExplain={
            item.explanation ? () => onToggleExplain(item.id) : undefined
          }
        />
      ))}
    </AgentBlock>
  );
}

function QuickReplyIcon({ title }: { title: string }) {
  const Icon =
    title === 'Reach target ACOS' ? TrendingDown : title === 'Automate bids & budgets' ? Zap : FilterX;
  return <Icon className="h-4 w-4 shrink-0 text-foreground-subtle" aria-hidden />;
}

function AgentBlock({
  time,
  badge,
  children,
}: {
  time: string;
  badge?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-ink text-chrome-text shadow-sm">
        <Bot className="h-5 w-5" aria-hidden />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-[16px] font-semibold leading-6">AdPilot AI</span>
          <span className="font-mono text-[11px] text-foreground-subtle">{time}</span>
          {badge ? (
            <span className="rounded-full bg-surface-muted px-2 py-0.5 text-[11px] font-medium text-foreground-muted">
              {badge}
            </span>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}

function AgentText({ time, text }: { time: string; text: string }) {
  return (
    <AgentBlock time={time}>
      <div className="max-w-3xl rounded-2xl rounded-tl-sm border border-border bg-surface p-4 text-body-lg shadow-sm">
        {text}
      </div>
    </AgentBlock>
  );
}

function UserBubble({
  name,
  initials,
  time,
  text,
}: {
  name: string;
  initials: string;
  time: string;
  text: string;
}) {
  return (
    <div className="flex items-start justify-end gap-3">
      <div className="flex max-w-2xl flex-col items-end gap-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] text-foreground-subtle">{time}</span>
          <span className="font-display text-[16px] font-semibold leading-6">{name}</span>
        </div>
        <div className="rounded-2xl rounded-tr-sm bg-brand-ink px-5 py-3 text-body-lg leading-relaxed text-white shadow-sm">
          “{text}”
        </div>
      </div>
      <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand text-[12px] font-semibold text-white shadow-sm">
        {initials}
      </div>
    </div>
  );
}

function RulesCard({
  agentActive,
  onActivate,
  onOpenGuardrails,
}: {
  agentActive: boolean;
  onActivate: () => void;
  onOpenGuardrails: () => void;
}) {
  const { rules } = useAgentDemo();
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-surface-muted px-5 py-3">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-5 w-5 text-brand" aria-hidden />
          <div>
            <h3 className="font-display text-[16px] font-semibold leading-6">Your proposed rules</h3>
            <p className="text-body-sm text-foreground-subtle">
              Synthesized from the conversation and sample account tolerances
            </p>
          </div>
        </div>
        <span className="rounded-full bg-brand-subtle px-2.5 py-0.5 font-mono text-[12px] font-semibold text-brand-ink">
          DRAFT · REV 1.0
        </span>
      </div>
      <div className="flex flex-col gap-3 p-5">
        {rules.map((rule) => (
          <RuleRow key={rule.id} rule={rule} />
        ))}
        <p className="text-[11px] italic text-foreground-subtle">
          Automatic execution follows these sample limits and can be paused from Guardrails. Nothing
          here is sent to Amazon.
        </p>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onOpenGuardrails}
            className="rounded-lg bg-surface-muted px-4 py-2 text-body-sm font-medium hover:bg-border"
          >
            Review guardrails
          </button>
          <button
            type="button"
            onClick={onActivate}
            disabled={agentActive}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-body-sm font-semibold shadow-sm',
              agentActive
                ? 'cursor-default bg-success-tint text-success-ink'
                : 'bg-brand-ink text-white hover:bg-brand-deep'
            )}
          >
            {agentActive ? (
              <Check className="h-4 w-4" aria-hidden />
            ) : (
              <Play className="h-4 w-4" aria-hidden />
            )}
            {agentActive ? 'Active & enforcing' : 'Activate agent'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RuleRow({ rule }: { rule: AgentRule }) {
  const Icon = RULE_ICONS[rule.id] ?? Crosshair;
  const iconClass =
    rule.tone === 'auto'
      ? 'bg-success-tint text-success-ink'
      : rule.tone === 'approval'
        ? 'bg-surface-muted text-foreground'
        : 'bg-brand-subtle text-brand';
  const rowClass =
    rule.tone === 'approval' ? 'bg-brand-subtle' : 'bg-surface-muted';

  return (
    <div className={cn('flex items-start justify-between gap-3 rounded-xl p-3', rowClass)}>
      <div className="flex min-w-0 items-start gap-3">
        <div className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', iconClass)}>
          <Icon className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display text-[13px] font-semibold">{rule.title}</span>
            {rule.value ? (
              <span
                className={cn(
                  'rounded px-2 py-0.5 font-mono text-[12px] font-semibold',
                  rule.tone === 'auto'
                    ? 'bg-success-tint text-success-ink'
                    : 'bg-surface text-foreground'
                )}
              >
                {rule.value}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-body-sm text-foreground-muted">{rule.body}</p>
        </div>
      </div>
      <span
        className={cn(
          'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold',
          rule.tone === 'auto' && 'bg-success-tint text-success-ink',
          rule.tone === 'approval' && 'bg-warning-tint text-warning-ink',
          rule.tone === 'goal' && 'bg-surface text-foreground-muted'
        )}
      >
        {rule.tone === 'auto' ? <Zap className="h-3.5 w-3.5" aria-hidden /> : null}
        {rule.tone === 'approval' ? <Lock className="h-3.5 w-3.5" aria-hidden /> : null}
        {rule.badge}
      </span>
    </div>
  );
}

function PulseCard() {
  const { account, pulse } = useAgentDemo();
  return (
    <div className="rounded-2xl border border-border bg-surface p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-brand" aria-hidden />
          <span className="font-display text-[13px] font-semibold">Portfolio pulse · Last 7 days</span>
        </div>
        <div className="flex items-center gap-1.5 text-body-sm text-foreground-muted">
          <span>ACOS trend</span>
          <span className="rounded bg-success-tint px-1.5 py-0.5 font-mono text-[12px] font-semibold text-success-ink">
            {pulse.trend}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <MetricTile label="Advertising spend" value={pulse.spend} delta={pulse.spendDelta} direction="down" />
        <MetricTile label="Ad attributed sales" value={pulse.sales} delta={pulse.salesDelta} direction="up" />
        <div className="flex flex-col justify-between rounded-xl bg-surface-muted p-3">
          <span className="text-[11px] font-medium text-foreground-muted">Actual ACOS</span>
          <div className="my-1 flex items-baseline gap-1.5">
            <span className="font-mono text-[20px] font-semibold">{pulse.acos}</span>
            <span className="font-mono text-[11px] text-foreground-subtle">/ Target {account.targetAcos}</span>
          </div>
          <span className="inline-flex items-center gap-1 font-mono text-[11px] text-brand">
            <Navigation className="h-3.5 w-3.5" aria-hidden />
            {pulse.acosNote}
          </span>
        </div>
        <MetricTile label="Blended ROAS" value={pulse.roas} delta={pulse.roasNote} direction="check" />
      </div>
      <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-surface-muted p-3">
        <div>
          <p className="text-[11px] text-foreground-subtle">Hourly Amazon stream volatility</p>
          <p className="font-mono text-[12px] font-medium">{pulse.forecast}</p>
        </div>
        <svg className="h-8 w-48 text-brand" fill="none" viewBox="0 0 160 30" aria-hidden>
          <path
            d="M 0,22 Q 25,24 45,18 T 80,14 T 115,10 T 160,4"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="2.5"
          />
          <circle cx="160" cy="4" r="3" fill="currentColor" />
        </svg>
      </div>
    </div>
  );
}

function MetricTile({
  label,
  value,
  delta,
  direction,
}: {
  label: string;
  value: string;
  delta: string;
  direction: 'up' | 'down' | 'check';
}) {
  const Icon = direction === 'up' ? ArrowUp : direction === 'down' ? ArrowDown : Check;
  return (
    <div className="flex flex-col justify-between rounded-xl bg-surface-muted p-3">
      <span className="text-[11px] font-medium text-foreground-muted">{label}</span>
      <span className="my-1 font-mono text-[20px] font-semibold">{value}</span>
      <span className="inline-flex items-center gap-1 font-mono text-[11px] text-success-ink">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {delta}
      </span>
    </div>
  );
}

function OpportunityCard({
  item,
  approved,
  explained,
  onApprove,
  onDismiss,
  onToggleExplain,
}: {
  item: AgentOpportunity;
  approved: boolean;
  explained: boolean;
  onApprove: () => void;
  onDismiss?: () => void;
  onToggleExplain?: () => void;
}) {
  const Icon = OPPORTUNITY_ICONS[item.id] ?? TrendingDown;
  const ReasonIcon = REASON_ICONS[item.id] ?? Brain;
  const iconClass =
    item.id === 'neg-1' ? 'bg-error-tint text-error' : 'bg-brand-subtle text-brand';

  return (
    <article className="flex flex-col gap-3 rounded-2xl border border-border bg-surface p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', iconClass)}>
            <Icon className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="font-display text-[16px] font-semibold leading-6">{item.title}</h4>
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                  approved
                    ? 'bg-success-tint text-success-ink'
                    : item.badge === 'auto'
                      ? 'bg-success-tint text-success-ink'
                      : 'bg-warning-tint text-warning-ink'
                )}
              >
                {approved ? <Check className="h-3 w-3" aria-hidden /> : <Zap className="h-3 w-3" aria-hidden />}
                {approved ? 'Approved' : item.badgeLabel}
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-foreground-subtle">{item.meta.join(' · ')}</p>
          </div>
        </div>
        <OpportunityChange item={item} />
      </div>
      <div className="flex flex-col justify-between gap-2 rounded-xl bg-surface-muted p-3 text-body-sm text-foreground-muted sm:flex-row sm:items-center">
        <p className="flex items-start gap-2">
          <ReasonIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden />
          <span>
            <strong className="font-medium text-foreground">{item.reasonLabel}: </strong>
            {item.reason}
          </span>
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <span className="rounded bg-surface px-2 py-0.5 font-mono text-[12px]">Conf: {item.confidence}</span>
          <span
            className={cn(
              'rounded px-2 py-0.5 text-[11px] font-semibold',
              item.riskTone === 'low'
                ? 'bg-success-tint text-success-ink'
                : 'bg-warning-tint text-warning-ink'
            )}
          >
            {item.risk}
          </span>
        </div>
      </div>
      <PastPerformance history={item.history} />
      {explained && item.explanation ? (
        <div className="space-y-1 rounded-xl border border-border bg-surface-muted p-4 text-body-sm">
          <p className="font-display text-[13px] font-semibold">Why this showed up</p>
          <p className="text-foreground-muted">{item.explanation}</p>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2 pt-1">
        {onToggleExplain ? (
          <button
            type="button"
            onClick={onToggleExplain}
            className="inline-flex items-center gap-1 text-[12px] font-medium text-brand hover:text-brand-deep"
          >
            <HelpCircle className="h-4 w-4" aria-hidden />
            {explained ? 'Hide explanation' : 'Explain why'}
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          {onDismiss && !approved ? (
            <button
              type="button"
              onClick={onDismiss}
              className="px-2 py-1.5 text-[12px] font-medium text-foreground-subtle hover:text-foreground"
            >
              {item.dismissLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onApprove}
            disabled={approved}
            className={cn(
              'inline-flex items-center gap-1 rounded-lg px-4 py-1.5 text-[12px] font-semibold shadow-sm',
              approved
                ? 'cursor-default bg-success-tint text-success-ink'
                : 'bg-brand-ink text-white hover:bg-brand-deep'
            )}
          >
            <Check className="h-3.5 w-3.5" aria-hidden />
            {approved ? 'Approved' : item.approveLabel}
          </button>
        </div>
      </div>
    </article>
  );
}

function PastPerformance({ history }: { history: AgentOpportunity['history'] }) {
  const totals = [
    { label: 'Spend', value: history.spend },
    { label: 'Sales', value: history.sales },
    { label: 'Clicks', value: history.clicks },
    { label: 'Orders', value: history.orders },
    { label: 'ACOS', value: history.acos },
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <div className="flex flex-wrap items-baseline justify-between gap-2 bg-surface-muted px-3 py-2">
        <span className="font-display text-[13px] font-semibold">Past performance</span>
        <span className="text-[11px] text-foreground-subtle">
          {history.window} · {history.entity}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-5">
        {totals.map((metric) => (
          <div key={metric.label} className="bg-surface px-3 py-2">
            <p className="text-[11px] text-foreground-subtle">{metric.label}</p>
            <p className="font-mono text-[13px] font-semibold">{metric.value}</p>
          </div>
        ))}
      </div>
      <div className="border-t border-border px-3 py-2">
        <p className="text-[11px] text-foreground-subtle">{history.rowsLabel}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-left text-[12px]">
          <thead>
            <tr className="border-y border-border text-[11px] text-foreground-subtle">
              <th className="px-3 py-1.5 font-medium">Day</th>
              <th className="px-3 py-1.5 font-medium">Spend</th>
              <th className="px-3 py-1.5 font-medium">Sales</th>
              <th className="px-3 py-1.5 font-medium">Clicks</th>
              <th className="px-3 py-1.5 font-medium">Orders</th>
              <th className="px-3 py-1.5 font-medium">ACOS</th>
            </tr>
          </thead>
          <tbody>
            {history.rows.map((row) => (
              <tr key={row.day} className="border-b border-border last:border-b-0">
                <td className="px-3 py-1.5">
                  <span className="font-medium">{row.day}</span>
                  {row.note ? (
                    <span className="mt-0.5 block text-[11px] text-warning-ink">{row.note}</span>
                  ) : null}
                </td>
                <td className="px-3 py-1.5 font-mono">{row.spend}</td>
                <td className="px-3 py-1.5 font-mono">{row.sales}</td>
                <td className="px-3 py-1.5 font-mono">{row.clicks}</td>
                <td className="px-3 py-1.5 font-mono">{row.orders}</td>
                <td className="px-3 py-1.5 font-mono">{row.acos}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OpportunityChange({ item }: { item: AgentOpportunity }) {
  if (item.wasteAmount) {
    return (
      <div className="shrink-0 text-right">
        <p className="font-mono text-[16px] font-bold text-error">{item.wasteAmount}</p>
        <p className="text-[11px] text-foreground-subtle">{item.wasteDetail}</p>
      </div>
    );
  }
  const positive = item.delta?.startsWith('+');
  return (
    <div className="flex shrink-0 items-center gap-2 font-mono">
      <span className="text-body-sm text-foreground-muted line-through">{item.from}</span>
      <span className="text-foreground-subtle" aria-hidden>
        →
      </span>
      <span className="text-[16px] font-bold">{item.to}</span>
      <span
        className={cn(
          'rounded px-1 text-[11px] font-semibold',
          positive ? 'bg-success-tint text-success-ink' : 'bg-brand-subtle text-brand-ink'
        )}
      >
        {item.delta}
      </span>
    </div>
  );
}

function Composer({
  draft,
  focusKey,
  onChange,
  onInsertPrompt,
  onSubmit,
}: {
  draft: string;
  focusKey: number;
  onChange: (value: string) => void;
  onInsertPrompt: (text: string) => void;
  onSubmit: (event: FormEvent) => void;
}) {
  const { promptChips } = useAgentDemo();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focusKey === 0) return;
    inputRef.current?.focus();
  }, [focusKey]);

  return (
    <div className="shrink-0 border-t border-border bg-surface/95 px-6 pb-3 pt-2 backdrop-blur">
      <div className="mx-auto flex max-w-4xl flex-col gap-1.5">
        <div className="flex gap-2 overflow-x-auto py-1">
          {promptChips.map((chip) => {
            const Icon = CHIP_ICONS[chip.label] ?? SlidersHorizontal;
            return (
              <button
                key={chip.label}
                type="button"
                onClick={() => onInsertPrompt(chip.prompt)}
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-surface px-3 py-1 text-body-sm font-medium shadow-sm hover:bg-brand-subtle"
              >
                <Icon className="h-3.5 w-3.5 text-brand" aria-hidden />
                {chip.label}
              </button>
            );
          })}
        </div>
        <form
          onSubmit={onSubmit}
          className="flex items-center gap-2 rounded-2xl border border-border bg-surface p-1.5 shadow-md"
        >
          <input
            ref={inputRef}
            value={draft}
            onChange={(event) => onChange(event.target.value)}
            placeholder="Ask anything or tell AdPilot to change a rule…"
            className="min-w-0 flex-1 bg-transparent px-2 text-body-md text-foreground outline-none placeholder:text-foreground-subtle"
          />
          <button
            type="submit"
            disabled={!draft.trim()}
            aria-label="Send instruction"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-ink text-white shadow-sm hover:bg-brand-deep disabled:opacity-40"
          >
            <ArrowUp className="h-4 w-4" aria-hidden />
          </button>
        </form>
        <p className="px-2 text-center font-mono text-[10.5px] leading-tight text-foreground-subtle">
          Demo only. Messages and approvals stay in this browser and are not sent to Amazon.
        </p>
      </div>
    </div>
  );
}

function GuardrailsDrawer({
  open,
  agentActive,
  hourlySync,
  onHourlySyncChange,
  onClose,
}: {
  open: boolean;
  agentActive: boolean;
  hourlySync: boolean;
  onHourlySyncChange: (next: boolean) => void;
  onClose: () => void;
}) {
  const { account, guardrails } = useAgentDemo();
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-brand-ink/20" onClick={onClose} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="guardrails-title"
        className="absolute right-0 top-0 flex h-full w-96 max-w-full flex-col bg-surface shadow-lg"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-5">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-subtle text-brand">
                <Shield className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <h3 id="guardrails-title" className="font-display text-[16px] font-semibold leading-6">
                  Active guardrails
                </h3>
                <p className="font-mono text-[11px] text-foreground-subtle">
                  {account.name} policy v1.4
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close guardrails"
              className="rounded-lg p-1 text-foreground-subtle hover:bg-surface-muted hover:text-foreground"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>

          <div className="flex flex-col gap-2 rounded-xl bg-surface-muted p-4">
            <div className="flex items-center justify-between">
              <span className="font-display text-[13px] font-semibold">Autonomous execution</span>
              <span className="rounded-full bg-brand-subtle px-2 py-0.5 font-mono text-[11px] font-semibold text-brand-ink">
                {agentActive ? 'Active' : 'Staged'}
              </span>
            </div>
            <p className="text-body-sm text-foreground-muted">
              When activated, low-risk opportunities that match the target ACOS rules are shown as
              ready for this demo. No changes are committed.
            </p>
            <div className="flex items-center justify-between pt-1">
              <span className="text-[11px] text-foreground-subtle">Hourly sync stream</span>
              <button
                type="button"
                role="switch"
                aria-checked={hourlySync}
                aria-label="Hourly sync stream"
                onClick={() => onHourlySyncChange(!hourlySync)}
                className={cn(
                  'relative h-5 w-9 rounded-full transition-colors',
                  hourlySync ? 'bg-brand' : 'bg-border'
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-surface shadow-sm transition-transform',
                    hourlySync && 'translate-x-4'
                  )}
                />
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-subtle">
              Safety constraints
            </span>
            {guardrails.filter((item) => item.id !== 'inventory').map((item) => (
              <GuardrailValue key={item.id} title={item.title} detail={item.detail} value={item.value} />
            ))}
            <div className="flex items-center justify-between rounded-xl bg-surface-muted p-3">
              <div>
                <p className="text-body-sm font-medium">Brand defense lock</p>
                <p className="text-body-sm text-foreground-subtle">Never drop bids on own brand terms</p>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full bg-success-tint px-2 py-1 text-[11px] font-semibold text-success-ink">
                <Lock className="h-3.5 w-3.5" aria-hidden />
                Locked
              </span>
            </div>
            {guardrails.filter((item) => item.id === 'inventory').map((item) => (
              <GuardrailValue key={item.id} title={item.title} detail={item.detail} value={item.value} />
            ))}
          </div>

          <div className="flex flex-col gap-2 rounded-xl bg-brand-subtle p-4">
            <div className="flex items-center gap-1.5 text-brand">
              <ShieldCheck className="h-4 w-4" aria-hidden />
              <span className="font-display text-[13px] font-semibold text-foreground">
                Amazon Marketing Stream
              </span>
            </div>
            <p className="text-body-sm text-foreground-muted">
              In the product, stream events would confirm that changes stay inside campaign bid caps
              and billing limits. This panel is sample copy.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 bg-surface-muted p-5">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg bg-brand-ink py-2.5 text-body-sm font-semibold text-white shadow-sm hover:bg-brand-deep"
          >
            Save & keep in sync
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg py-2 text-[12px] font-medium text-foreground-subtle hover:text-foreground"
          >
            Dismiss
          </button>
        </div>
      </aside>
    </div>
  );
}

function GuardrailValue({
  title,
  detail,
  value,
}: {
  title: string;
  detail: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-surface-muted p-3">
      <div>
        <p className="text-body-sm font-medium">{title}</p>
        <p className="text-body-sm text-foreground-subtle">{detail}</p>
      </div>
      <span className="shrink-0 rounded bg-surface px-2 py-1 font-mono text-[13px] font-bold">{value}</span>
    </div>
  );
}
